import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const EditSessionModal = ({ session, onSave, onClose, isLoading = false }) => {
  const [formData, setFormData] = useState({
    date: '',
    activityType: '',
    minutes: '',
    activeTime: '',
    rpe: 5,
    notes: ''
  });
  const [errors, setErrors] = useState({});

  const activityOptions = [
    { value: 'rest_day', label: 'Rest Day' },
    { value: 'running', label: 'Running' },
    { value: 'cycling', label: 'Cycling' },
    { value: 'swimming', label: 'Swimming' },
    { value: 'strength', label: 'Strength Training' },
    { value: 'football', label: 'Football' },
    { value: 'basketball', label: 'Basketball' },
    { value: 'tennis', label: 'Tennis' },
    { value: 'yoga', label: 'Yoga' },
    { value: 'other', label: 'Other' }
  ];

  const rpeLabels = {
    1: 'Very Easy',
    2: 'Easy',
    3: 'Moderate',
    4: 'Somewhat Hard',
    5: 'Hard',
    6: 'Very Hard',
    7: 'Extremely Hard',
    8: 'Maximum Effort',
    9: 'Near Maximum',
    10: 'Maximum'
  };

  // Initialize form with session data
  useEffect(() => {
    if (session) {
      setFormData({
        date: session?.date?.split('T')?.[0] || '',
        activityType: session?.activityType || '',
        minutes: session?.minutes?.toString() || '',
        activeTime: (session?.activeTime || session?.minutes)?.toString() || '',
        rpe: session?.rpe || 5,
        notes: session?.notes || ''
      });
    }
  }, [session]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.activityType) {
      newErrors.activityType = 'Please select an activity';
    }
    
    const totalTime = parseInt(formData?.minutes);
    const activeTime = parseInt(formData?.activeTime);
    const isRestDay = formData?.activityType === 'rest_day';
    
    if (isNaN(totalTime) || totalTime < 0) {
      newErrors.minutes = 'Please enter a valid total time';
    }
    
    if (isNaN(activeTime) || activeTime < 0) {
      newErrors.activeTime = 'Please enter a valid active time';
    }
    
    if (!isRestDay && totalTime < 1) {
      newErrors.minutes = 'Total time must be at least 1 minute';
    }
    
    if (!isRestDay && activeTime < 1) {
      newErrors.activeTime = 'Active time must be at least 1 minute';
    }
    
    if (activeTime > totalTime) {
      newErrors.activeTime = 'Active time cannot exceed total time';
    }
    
    if (totalTime > 600) {
      newErrors.minutes = 'Duration cannot exceed 10 hours';
    }
    
    if (formData?.rpe < 1 || formData?.rpe > 10) {
      newErrors.rpe = 'RPE must be between 1 and 10';
    }
    
    const selectedDate = new Date(formData?.date);
    const today = new Date();
    today?.setHours(23, 59, 59, 999);
    
    if (selectedDate > today) {
      newErrors.date = 'Cannot log sessions for future dates';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-populate fields when Rest Day is selected
      if (field === 'activityType' && value === 'rest_day') {
        updated.minutes = '0';
        updated.activeTime = '0';
        updated.rpe = 1;
      }
      
      return updated;
    });
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const totalTime = parseInt(formData?.minutes);
    const activeTime = parseInt(formData?.activeTime);
    const load = activeTime * formData?.rpe;
    
    const updatedSession = {
      ...session,
      date: formData?.date,
      activityType: formData?.activityType,
      minutes: totalTime,
      activeTime: activeTime,
      rpe: formData?.rpe,
      load: load,
      notes: formData?.notes
    };
    
    await onSave(updatedSession);
  };

  const calculatedLoad = (parseInt(formData?.activeTime) || 0) * (formData?.rpe || 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-surface border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Edit" size={20} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Edit Session</h2>
              <p className="text-sm text-text-secondary">Update session details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-muted rounded-lg smooth-transition"
            disabled={isLoading}
          >
            <Icon name="X" size={20} color="currentColor" strokeWidth={2} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Date
            </label>
            <Input
              type="date"
              value={formData?.date}
              onChange={(e) => handleInputChange('date', e?.target?.value)}
              error={errors?.date}
              disabled={isLoading}
            />
          </div>

          {/* Activity Type */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Activity Type
            </label>
            <Select
              value={formData?.activityType}
              onChange={(value) => handleInputChange('activityType', value)}
              options={activityOptions}
              error={errors?.activityType}
              disabled={isLoading}
            />
          </div>

          {/* Total Time and Active Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Total Time (minutes)
              </label>
              <Input
                type="number"
                min="0"
                max="600"
                value={formData?.minutes}
                onChange={(e) => handleInputChange('minutes', e?.target?.value)}
                error={errors?.minutes}
                disabled={isLoading}
                placeholder="e.g., 60"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Active Time (minutes)
              </label>
              <Input
                type="number"
                min="0"
                max="600"
                value={formData?.activeTime}
                onChange={(e) => handleInputChange('activeTime', e?.target?.value)}
                error={errors?.activeTime}
                disabled={isLoading}
                placeholder="e.g., 45"
              />
            </div>
          </div>

          {/* RPE Slider */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              RPE (Rate of Perceived Exertion)
            </label>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-primary">{formData?.rpe}</span>
                <span className="text-sm text-text-secondary">{rpeLabels?.[formData?.rpe]}</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={formData?.rpe}
                onChange={(e) => handleInputChange('rpe', parseInt(e?.target?.value))}
                disabled={isLoading}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${(formData?.rpe - 1) * 11.11}%, var(--color-muted) ${(formData?.rpe - 1) * 11.11}%, var(--color-muted) 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-text-secondary">
                <span>1 - Very Easy</span>
                <span>10 - Maximum</span>
              </div>
            </div>
            {errors?.rpe && (
              <p className="text-sm text-error mt-1">{errors?.rpe}</p>
            )}
          </div>

          {/* Calculated Load */}
          <div className="bg-muted/30 border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="TrendingUp" size={20} color="var(--color-primary)" strokeWidth={2} />
                <span className="text-sm font-medium text-text-secondary">Training Load</span>
              </div>
              <div className="text-2xl font-bold text-primary">
                {calculatedLoad}
                <span className="text-sm text-text-secondary ml-1">AU</span>
              </div>
            </div>
            <p className="text-xs text-text-secondary mt-2">
              Active Time × RPE = {formData?.activeTime || 0} × {formData?.rpe} = {calculatedLoad} AU
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              disabled={isLoading}
              placeholder="Add any additional notes about this session..."
              rows={3}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/50 smooth-transition resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Icon name="Loader" size={16} color="currentColor" strokeWidth={2} className="animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} color="currentColor" strokeWidth={2} />
                  <span>Save Changes</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSessionModal;